import { IonAlert, IonContent, IonHeader, IonPage, IonTitle, IonToast, IonToolbar } from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../api/client.js";
import { fetchUsers } from "../api/users.js";
import { useAuth } from "../auth/AuthContext.jsx";
import DateNavigator from "../components/DateNavigator.jsx";
import ExpandedJobCard from "../components/ExpandedJobCard.jsx";
import ScheduleSearchModal from "../components/ScheduleSearchModal.jsx";
import Timeline from "../components/Timeline.jsx";
import { addDays, dateInput } from "../utils/date.js";

export default function SchedulePage() {
  const { session } = useAuth();
  const [date, setDate] = useState(new Date());
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightedJobId, setHighlightedJobId] = useState(null);
  const [pendingTimerJob, setPendingTimerJob] = useState(null);
  const [pendingTimerRunning, setPendingTimerRunning] = useState(false);
  const [submittingTimerAction, setSubmittingTimerAction] = useState(null);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);
  const submittingTimerActionRef = useRef(false);
  const deletingScheduleRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const currentUserId = session?.user?.id;

  const query = useMemo(() => {
    const params = new URLSearchParams({
      date: dateInput(date),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Brisbane",
    });

    return `?${params.toString()}`;
  }, [date]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextScheduledJobs, nextUsers] = await Promise.all([
        apiFetch(`/schedule${query}`),
        session?.role === "admin" ? fetchUsers() : Promise.resolve([]),
      ]);

      setScheduledJobs(nextScheduledJobs);
      setUsers(nextUsers);

      return nextScheduledJobs;
    } catch (err) {
      setToast(err.message);

      return null;
    } finally {
      setLoading(false);
    }
  }, [query, session?.role]);

  useEffect(() => {
    load();
  }, [load]);

  function selectSearchResult(occurrence) {
    setDate(new Date(occurrence.scheduledStart));
    setHighlightedJobId(occurrence.id);
  }

  async function submitTimerAction(job, action) {
    if (submittingTimerActionRef.current) {
      return;
    }

    submittingTimerActionRef.current = true;
    setSubmittingTimerAction(action);

    try {
      await apiFetch(`/time-entries/${action}`, { method: "POST", body: { scheduleOccurrenceId: job.id } });
      const refreshedJobs = await load();
      const refreshedJob = refreshedJobs?.find((candidate) => candidate.id === job.id);

      setSelectedJob((current) => current?.id === job.id ? (refreshedJob || current) : current);
      setPendingTimerJob(null);
      setPendingTimerRunning(false);
    } catch (err) {
      setToast(err.message);
    } finally {
      submittingTimerActionRef.current = false;
      setSubmittingTimerAction(null);
    }
  }

  function toggleTimer(job, running) {
    if (!currentUserId) {
      setToast("Only assigned team members can track time.");

      return;
    }

    setPendingTimerJob(job);
    setPendingTimerRunning(running);
  }

  async function updateScheduledJob(jobId, payload, allocations, payAllocations) {
    try {
      const updated = await apiFetch(`/schedule/${jobId}`, { method: "PUT", body: payload });

      const allocationRequests = [];

      if (payAllocations !== undefined) {
        allocationRequests.push(apiFetch(`/schedule/pay-allocation/${updated.id}`, {
          method: "PUT",
          body: { allocations: payAllocations },
        }));
      }

      if (allocations !== undefined) {
        allocationRequests.push(apiFetch(`/time-entries/allocation/${updated.id}`, {
          method: "PUT",
          body: { allocations },
        }));
      }

      await Promise.all(allocationRequests);
      const refreshedJobs = await load();
      const refreshedJob = refreshedJobs?.find((candidate) => candidate.id === updated.id);

      setSelectedJob(refreshedJob || updated);
      setToast("Scheduled job updated.");
    } catch (err) {
      setToast(err.message);
      throw err;
    }
  }

  async function cancelScheduledJob(jobId) {
    try {
      const updated = await apiFetch(`/schedule/cancel/${jobId}`, { method: "POST" });

      setSelectedJob(updated);
      await load();
      setToast("Scheduled job cancelled.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function deleteScheduledJob(jobId) {
    if (deletingScheduleRef.current) {
      return;
    }

    deletingScheduleRef.current = true;
    setDeletingScheduleId(jobId);
    try {
      await apiFetch(`/schedule/${jobId}`, { method: "DELETE" });
      setSelectedJob(null);
      await load();
      setToast("Scheduled job removed.");
    } catch (err) {
      setToast(err.message);
    } finally {
      deletingScheduleRef.current = false;
      setDeletingScheduleId(null);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Schedule</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <main className="page-pad">
          <DateNavigator
            date={date}
            onChange={(days) => {
              setHighlightedJobId(null);
              setDate(days === 0 ? new Date() : addDays(date, days));
            }}
            onSearch={() => setIsSearchOpen(true)}
          />
          {!loading && (
            <Timeline
              jobs={scheduledJobs}
              currentUserId={currentUserId}
              canControlAnyTimer={session?.role === "admin"}
              highlightedJobId={highlightedJobId}
              onHighlightComplete={() => setHighlightedJobId(null)}
              onOpenJob={setSelectedJob}
              onToggleTimer={toggleTimer}
            />
          )}
        </main>
        <ScheduleSearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          onSelect={selectSearchResult}
        />
        <ExpandedJobCard
          job={selectedJob}
          currentUserId={currentUserId}
          isOpen={Boolean(selectedJob)}
          canEditSchedule={session?.role === "admin"}
          users={users}
          onClose={() => setSelectedJob(null)}
          onToggleTimer={toggleTimer}
          onUpdateSchedule={updateScheduledJob}
          onCancelSchedule={cancelScheduledJob}
          onDeleteSchedule={deleteScheduledJob}
          isDeletingSchedule={deletingScheduleId === selectedJob?.id}
        />
        <IonAlert
          cssClass="metora-alert"
          isOpen={Boolean(pendingTimerJob) && !pendingTimerRunning}
          backdropDismiss={!submittingTimerAction}
          header="Start Timer"
          message="This timer will become the payroll duration for all standard employees assigned to this scheduled job. Client invoicing will continue to use the estimated duration."
          buttons={[
            { text: "Cancel", role: "cancel", htmlAttributes: { disabled: Boolean(submittingTimerAction) } },
            {
              text: submittingTimerAction === "start" ? "Starting…" : "Start",
              htmlAttributes: { disabled: Boolean(submittingTimerAction) },
              handler: () => {
                void submitTimerAction(pendingTimerJob, "start");

                return false;
              },
            },
          ]}
          onDidDismiss={() => {
            if (submittingTimerActionRef.current) {
              return;
            }

            setPendingTimerJob(null);
            setPendingTimerRunning(false);
          }}
        />
        <IonAlert
          cssClass="metora-alert"
          isOpen={Boolean(pendingTimerJob) && pendingTimerRunning}
          backdropDismiss={!submittingTimerAction}
          header="Timer"
          message="Pause keeps the scheduled job active. Finish stops the active timer and marks this scheduled job complete."
          buttons={[
            { text: "Cancel", role: "cancel", htmlAttributes: { disabled: Boolean(submittingTimerAction) } },
            {
              text: submittingTimerAction === "pause" ? "Pausing…" : "Pause",
              htmlAttributes: { disabled: Boolean(submittingTimerAction) },
              handler: () => {
                void submitTimerAction(pendingTimerJob, "pause");

                return false;
              },
            },
            {
              text: submittingTimerAction === "finish" ? "Finishing…" : "Finish",
              role: "destructive",
              htmlAttributes: { disabled: Boolean(submittingTimerAction) },
              handler: () => {
                void submitTimerAction(pendingTimerJob, "finish");

                return false;
              },
            },
          ]}
          onDidDismiss={() => {
            if (submittingTimerActionRef.current) {
              return;
            }

            setPendingTimerJob(null);
            setPendingTimerRunning(false);
          }}
        />
        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
